/**
 * #989 — honest coverage for the Polymarket US portfolio, alongside the
 * Position Tracker's Kalshi and international Polymarket venues (#904).
 *
 * Modelled on src/views/PositionTracker/venueReadiness.ts, with two US
 * differences:
 * - `incomplete`: the US portfolio collector finished without covering every
 *   page. Its rows may be shown, but never certify a total or read as empty.
 * - `failed` may still be `readable`: stored receipts stay visible after
 *   credentials or cancel rights are disabled. Such rows are shown but the
 *   venue stays EXCLUDED from every total and coverage label.
 *
 * Readiness (may I submit or cancel) and coverage (did the read cover the whole
 * account) are separate. This module only answers the second.
 */
import type { UsCoverage, UsPortfolioSnapshot } from './types';
import { presentUsOrder, usOrderKey, usPositionKey } from './portfolioModel';

export type UsCoverageState = 'loading' | 'ready' | 'confirmed-empty' | 'incomplete' | 'stale' | 'failed';

export type UsVenueCoverage = {
  state: UsCoverageState;
  /** Rows may be displayed. */
  readable: boolean;
  /** The latest read covered the whole account, so its totals are complete. */
  confirmed: boolean;
  reason: string | null;
  /** When the displayed evidence was observed; null when there is none. */
  observedAtMs: number | null;
};

export const US_COVERAGE_LABEL: Record<UsCoverageState, string> = {
  loading: 'loading',
  ready: 'recorded',
  'confirmed-empty': 'recorded (empty)',
  incomplete: 'incomplete',
  stale: 'refresh failed',
  failed: 'unavailable',
};

export type UsReadState = {
  /** The last snapshot accepted for the current scope, if any. */
  snapshot: UsPortfolioSnapshot | null;
  /** Whose orders were read. A desk read never includes holdings. */
  view?: 'own' | 'desk';
  loading: boolean;
  /** The latest read's failure, if it failed. */
  error: string | null;
};

export function usVenueCoverage({ snapshot, loading, error, view }: UsReadState): UsVenueCoverage {
  if (!snapshot) {
    if (error) return { state: 'failed', readable: false, confirmed: false, reason: error, observedAtMs: null };
    return { state: 'loading', readable: false, confirmed: false, reason: loading ? null : 'Not read yet', observedAtMs: null };
  }
  const coverage = snapshot.coverage;
  if (coverage.state === 'unavailable') {
    // Stored receipts stay visible after credentials or cancel rights are
    // disabled (docs/260927PolymarketUsCancellation.md); they are never counted.
    const retained = snapshot.orders.length + snapshot.positions.length > 0;
    return { state: 'failed', readable: retained, confirmed: false, reason: coverage.reason, observedAtMs: null };
  }
  // A failed refresh keeps the last rows on screen, uncertified.
  if (error) return { state: 'stale', readable: true, confirmed: false, reason: error, observedAtMs: coverage.observedAtMs };
  if (coverage.state === 'incomplete') {
    return { state: 'incomplete', readable: true, confirmed: false, reason: coverage.reason, observedAtMs: coverage.observedAtMs };
  }
  // Separate order and holdings coverage, when the adapter supplies it: either
  // side incomplete withholds BOTH totals (September 28 decision), even if the
  // combined flag claims complete. A full order page never certifies holdings.
  const sections = usSectionCoverage(snapshot, view);
  const weak = sections && [sections.orders, sections.holdings].find((c) => c.state !== 'complete');
  if (weak) {
    return {
      state: 'incomplete',
      readable: true,
      confirmed: false,
      reason: weak.reason,
      observedAtMs: coverage.observedAtMs,
    };
  }
  const duplicate = hasDuplicateIdentity(snapshot);
  if (duplicate) {
    return { state: 'incomplete', readable: true, confirmed: false, reason: duplicate, observedAtMs: coverage.observedAtMs };
  }
  const empty = snapshot.orders.length === 0 && snapshot.positions.length === 0;
  return { state: empty ? 'confirmed-empty' : 'ready', readable: true, confirmed: true, reason: null, observedAtMs: coverage.observedAtMs };
}

const DESK_HOLDINGS: UsCoverage = { state: 'unavailable', reason: 'US_DESK_INVENTORY_UNAVAILABLE' };

/**
 * Orders and holdings coverage as the adapter reported them; null when it
 * reported only the combined flag. A desk read never covers holdings (the
 * viewer's own inventory is never substituted), whatever the adapter sent.
 */
export function usSectionCoverage(
  snapshot: UsPortfolioSnapshot,
  view: 'own' | 'desk' = 'own',
): { orders: UsCoverage; holdings: UsCoverage } | null {
  const details = snapshot.readDetails;
  if (view === 'desk') return { orders: details?.orders ?? snapshot.coverage, holdings: DESK_HOLDINGS };
  return details ? { orders: details.orders, holdings: details.inventory } : null;
}

/** Identity is scope + id; the same row twice can't be trusted, so it can't be counted. */
function hasDuplicateIdentity(snapshot: UsPortfolioSnapshot): string | null {
  const orders = new Set(snapshot.orders.map(usOrderKey));
  if (orders.size !== snapshot.orders.length) return 'US_DUPLICATE_ORDER_IDENTITY';
  const positions = new Set(snapshot.positions.map(usPositionKey));
  if (positions.size !== snapshot.positions.length) return 'US_DUPLICATE_POSITION_IDENTITY';
  return null;
}

// ── Combined coverage across venues ────────────────────────────────────────

/**
 * - `included`: rows shown and the read is complete.
 * - `unconfirmed`: rows shown, but the read did not certify a complete total.
 * - `excluded`: not part of any total (unreadable, or unavailable with only
 *   retained rows on screen).
 */
export type VenueCoverageStanding = 'included' | 'unconfirmed' | 'excluded';

export type VenueCoverageEntry = {
  /** Short code for the label, e.g. 'K', 'P', 'US'. */
  code: string;
  venue: string;
  standing: VenueCoverageStanding;
  reason: string | null;
};

export function usCoverageEntry(coverage: UsVenueCoverage): VenueCoverageEntry {
  const standing: VenueCoverageStanding =
    coverage.state === 'failed' || !coverage.readable ? 'excluded' : coverage.confirmed ? 'included' : 'unconfirmed';
  return { code: 'US', venue: 'Polymarket US', standing, reason: coverage.reason };
}

export type CombinedCoverage = {
  /** Venues counted in totals (complete or not), e.g. "K+P+US"; "none" when none are. */
  label: string;
  /** Every venue's latest read is complete. */
  complete: boolean;
  /** Not readable: left out of every total. */
  excluded: { venue: string; reason: string | null }[];
  /** Shown, but its read did not certify a complete total. */
  unconfirmed: { venue: string; reason: string | null }[];
};

export function combineVenueCoverage(entries: readonly VenueCoverageEntry[]): CombinedCoverage {
  const counted = entries.filter((e) => e.standing !== 'excluded');
  const pick = (standing: VenueCoverageStanding) =>
    entries.filter((e) => e.standing === standing).map(({ venue, reason }) => ({ venue, reason }));
  return {
    label: counted.length ? counted.map((e) => e.code).join('+') : 'none',
    complete: entries.length > 0 && entries.every((e) => e.standing === 'included'),
    excluded: pick('excluded'),
    unconfirmed: pick('unconfirmed'),
  };
}

// ── US totals ──────────────────────────────────────────────────────────────

const DECIMAL = /^\d+(\.\d+)?$/;

/** Exact sum of non-negative decimal strings; null if any is missing or malformed. */
export function sumDecimalText(values: readonly (string | null)[]): string | null {
  let scale = 0;
  for (const v of values) {
    if (v === null || !DECIMAL.test(v)) return null;
    scale = Math.max(scale, v.split('.')[1]?.length ?? 0);
  }
  let total = 0n;
  for (const v of values as string[]) {
    const [whole, fraction = ''] = v.split('.');
    total += BigInt(whole + fraction.padEnd(scale, '0'));
  }
  const digits = total.toString().padStart(scale + 1, '0');
  if (!scale) return digits;
  const fraction = digits.slice(-scale).replace(/0+$/, '');
  return digits.slice(0, -scale) + (fraction ? `.${fraction}` : '');
}

export type UsTotals = {
  /** Orders not yet terminal. Null when the read is not complete. */
  openOrders: number | null;
  /**
   * Contracts held, summed from POSITION rows only. An order's filled
   * quantity is already inside its position and is never added again.
   * Null when the read is not complete, or any holding is unknown, partly
   * known or negative.
   */
  heldContracts: string | null;
};

export function usTotals(snapshot: UsPortfolioSnapshot | null, coverage: UsVenueCoverage): UsTotals {
  if (!snapshot || !coverage.confirmed) return { openOrders: null, heldContracts: null };
  return {
    openOrders: snapshot.orders.filter((o) => !presentUsOrder(o).terminal
      && (o.lifecycle !== null || o.receipt.status !== 'ready')).length,
    heldContracts: snapshot.positions.some((p) => p.reasons.length > 0)
      ? null
      : sumDecimalText(snapshot.positions.map((p) => p.heldQuantity)),
  };
}
